import { ActionDetailsWrapper } from "@app/components/actions/ActionDetailsWrapper";
import type {
  ActionDetailsDisplayContext,
  ToolExecutionDetailsProps,
} from "@app/components/actions/mcp/details/types";
import { isGenerateImageInputType } from "@app/lib/actions/mcp_internal_actions/types";
import { useFileMetadata } from "@app/lib/swr/files";
import type { AgentMCPActionWithOutputType } from "@app/types/actions";
import { stripFileExtension } from "@app/types/files";
import type { LightWorkspaceType } from "@app/types/user";
import { Chip, cn, Image01, Separator } from "@dust-tt/sparkle";
import React from "react";
import { useTranslation } from "react-i18next";

// "high" is retained for historical actions generated before the 4K tier was
// removed from the agent-facing tool; the write path is now capped at "medium".
const QUALITY_LABELS: Record<string, string> = {
  low: "1K",
  medium: "2K",
  high: "4K",
};

interface ReferenceImageChipProps {
  fileId: string;
  owner: LightWorkspaceType;
}

function ReferenceImageChip({ fileId, owner }: ReferenceImageChipProps) {
  const { t } = useTranslation();
  const { fileMetadata, isFileMetadataLoading } = useFileMetadata({
    fileId,
    owner,
  });

  const label = isFileMetadataLoading
    ? t("assistant.mcpLoadingEllipsis", "Loading...")
    : (fileMetadata?.fileName ?? fileId);

  return <Chip size="xs" color="highlight" label={label} />;
}

export function MCPImageGenerationActionDetails({
  displayContext,
  toolParams,
  owner,
}: ToolExecutionDetailsProps) {
  const { t } = useTranslation();
  if (!isGenerateImageInputType(toolParams)) {
    return (
      <ActionDetailsWrapper
        displayContext={displayContext}
        actionName={
          displayContext === "conversation"
            ? t("assistant.mcpGeneratingImage", "Generating image")
            : t("assistant.mcpGenerateImage", "Generate image")
        }
        visual={Image01}
      />
    );
  }

  const { prompt, outputName, aspectRatio, referenceImages, quality } =
    toolParams;

  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={
        displayContext === "conversation"
          ? t("assistant.mcpGeneratingImage", "Generating image")
          : t("assistant.mcpGenerateImage", "Generate image")
      }
      visual={Image01}
    >
      <div
        className={cn(
          "flex flex-col gap-3",
          displayContext === "conversation" ? "pl-6" : "pt-2"
        )}
      >
        <div className="flex flex-wrap gap-1">
          {referenceImages &&
            referenceImages.length > 0 &&
            referenceImages.map((fileId) => (
              <ReferenceImageChip key={fileId} fileId={fileId} owner={owner} />
            ))}
          {outputName && (
            <Chip
              size="xs"
              color="success"
              label={stripFileExtension(outputName)}
            />
          )}
          {aspectRatio && <Chip size="xs" label={aspectRatio} />}
          {quality && (
            <Chip
              size="xs"
              label={t("assistant.mcpImageQualityLabel", {
                defaultValue: "{{quality}} quality",
                quality: QUALITY_LABELS[quality],
              })}
            />
          )}
        </div>
        <p
          className={cn(
            "text-sm text-muted-foreground",
            displayContext === "conversation" ? "line-clamp-3" : ""
          )}
        >
          {prompt}
        </p>
      </div>
    </ActionDetailsWrapper>
  );
}

interface MCPImageGenerationGroupedDetailsProps {
  displayContext: ActionDetailsDisplayContext;
  actions: AgentMCPActionWithOutputType[];
  owner: LightWorkspaceType;
}

export function MCPImageGenerationGroupedDetails({
  displayContext,
  actions,
  owner,
}: MCPImageGenerationGroupedDetailsProps) {
  const { t } = useTranslation();
  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={t("assistant.mcpGeneratingNImages", {
        defaultValue: "Generating {{count}} images",
        count: actions.length,
      })}
      visual={Image01}
    >
      <div
        className={cn(
          "flex flex-col gap-4",
          displayContext === "conversation" ? "pl-6" : "pt-2"
        )}
      >
        {actions.map((action, index) => {
          const params = action.params;
          if (!isGenerateImageInputType(params)) {
            return null;
          }
          const { prompt, outputName, aspectRatio, referenceImages, quality } =
            params;
          return (
            <React.Fragment key={action.id}>
              {index > 0 && <Separator />}
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-1">
                  {referenceImages &&
                    referenceImages.length > 0 &&
                    referenceImages.map((fileId) => (
                      <ReferenceImageChip
                        key={fileId}
                        fileId={fileId}
                        owner={owner}
                      />
                    ))}
                  {outputName && (
                    <Chip
                      size="xs"
                      color="success"
                      label={stripFileExtension(outputName)}
                    />
                  )}
                  {aspectRatio && <Chip size="xs" label={aspectRatio} />}
                  {quality && (
                    <Chip
                      size="xs"
                      label={t("assistant.mcpImageQualityLabel", {
                        defaultValue: "{{quality}} quality",
                        quality: QUALITY_LABELS[quality],
                      })}
                    />
                  )}
                </div>
                <p
                  className={cn(
                    "text-sm text-muted-foreground",
                    displayContext === "conversation" ? "line-clamp-3" : ""
                  )}
                >
                  {prompt}
                </p>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </ActionDetailsWrapper>
  );
}
